import * as React from 'react';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { closeText } from 'owa-locstrings/lib/strings/closetext.locstring.json';
import ColorScheme, { colorSchemeToCss } from 'owa-color-utils/lib/ColorScheme';
import { ControlIcons } from 'owa-control-icons';
import DataRow from 'owa-calendar-forms-common/lib/components/DataRow';
import { getCalendarEventItemSubjectText } from 'owa-calendar-event-attributes';
import { getOptimalTextColorString } from 'owa-color-utils/lib/colorUtils';
import { getPaletteAsRawColors } from 'owa-theme';
import EnhancedLocation from 'owa-service/lib/contract/EnhancedLocation';
import fetchCalendarEventDetails from '../actions/fetchCalendarEventDetails';
import { FocusTrapZone } from '@fluentui/react/lib/FocusTrapZone';
import formatMeetingTimeSpan from 'owa-timeformat/lib/utils/formatMeetingTimeSpan';
import generateLinkStyle from 'owa-component-styling-utils/lib/utils/colorSchemesToStyles/generateLinkStyle';
import { getCalendarEventColorScheme } from 'owa-calendar-color-resolver';
import { hasVisibleContentInBody } from 'owa-calendar-forms-utils/lib/utils/hasVisibleContentInBody';
import HtmlSanitizer from 'roosterjs-editor-dom/lib/htmlSanitizer/HtmlSanitizer';
import { Icon } from '@fluentui/react/lib/Icon';
import { IconButton } from '@fluentui/react/lib/Button';
import { titleLabelAccessibility } from 'owa-calendar-shared-strings/lib/strings/calendarEventTitleAndCharm.locstring.json';
import { infobarViewSeries } from 'owa-calendar-shared-strings/lib/strings/calendarDetailsPanel.locstring.json';
import InlineLocations from 'owa-calendar-forms-common/lib/components/InlineLocations';
import { isException, isOccurrence } from 'owa-calendar-event-capabilities';
import { Link } from '@fluentui/react/lib/Link';
import loc from 'owa-localize';
import { locationAccessibility } from 'owa-locstrings/lib/strings/locationaccessibility.locstring.json';
import { MessageBody } from 'owa-controls-content-handler';
import { observer } from 'mobx-react-lite';
import { toCalendarItem } from 'owa-calendar-event-converter';

const styles = require('./CalendarReadingPane.scss');
const classNames = require('classnames/bind').bind(styles);

export interface CalendarReadingPaneProps {
    item: CalendarEvent;
    dimissReadingPane: () => void;
}

/**
 * Read-only calendar event details panel
 */
export default observer(function CalendarReadingPane(props: CalendarReadingPaneProps) {
    const { item, dimissReadingPane } = props;
    const colorScheme = getCalendarEventColorScheme(item);
    const calendarColorStyle = colorSchemeToCss(colorScheme);
    const buttonStyles = {
        root: {
            fontSize: '17px',
            color: colorScheme.accessibleText.calPrimaryBackground,
        },
    };

    const textColor = getOptimalTextColorString(
        getPaletteAsRawColors().white,
        colorScheme,
        colorScheme.calPrimary
    );
    const title = getCalendarEventItemSubjectText(item);

    let locations: EnhancedLocation[] = [];

    // All locations are passed as a single location from the anonymous calendar service
    if (item.Location && item.Location.DisplayName !== '') {
        locations.push(item.Location);
    }

    const santitizedHTMLBody = HtmlSanitizer.convertInlineCss(item.Body?.Value);

    return (
        <FocusTrapZone className={classNames(styles.container, styles.contentWidthLimits)}>
            <div
                className={styles.calendarBar}
                style={{ backgroundColor: calendarColorStyle.backgroundColor }}>
                <span className={styles.calendarBarFiller} />
                <IconButton // Close button
                    title={loc(closeText)}
                    ariaLabel={loc(closeText)}
                    onClick={dimissReadingPane}
                    className={classNames(styles.calendarBarIcon)}>
                    <Icon iconName={ControlIcons.Cancel} styles={buttonStyles} />
                </IconButton>
            </div>
            <div className={classNames(styles.content, styles.contentWidthLimits)}>
                <div className={styles.scrollContainer}>
                    <div className={styles.titleRow}>
                        <span // Title
                            style={{ color: textColor }}
                            aria-label={loc(titleLabelAccessibility)}
                            className={classNames(styles.titleText, 'allowTextSelection')}>
                            {title}
                        </span>
                    </div>
                    <div className={styles.detailsPanel}>
                        <DataRow // When
                            iconName={ControlIcons.Clock}
                            className={styles.time}
                            iconClassName={styles.whenIcon}
                            iconContainerClassName={styles.iconContainer}>
                            <div className={styles.whenRow}>
                                {formatMeetingTimeSpan(item.Start, item.End, item.Recurrence)}
                                {(isOccurrence(item.CalendarItemType) ||
                                    isException(item.CalendarItemType)) && (
                                    <Link
                                        onClick={onViewSeriesClicked(item)}
                                        className={styles.viewSeries}
                                        styles={generateLinkStyle(colorScheme)}>
                                        <Icon
                                            iconName={ControlIcons.SyncOccurence}
                                            className={styles.viewSeriesIcon}
                                        />
                                        {loc(infobarViewSeries)}
                                    </Link>
                                )}
                            </div>
                        </DataRow>
                        {locations.length > 0 && (
                            <DataRow // Where
                                iconName={ControlIcons.POI}
                                className={styles.location}
                                iconClassName={styles.whereIcon}
                                iconContainerClassName={styles.iconContainer}
                                childrenAriaLabel={loc(locationAccessibility)}>
                                <div className={styles.whereRow}>
                                    {renderLocation(locations, item, colorScheme)}
                                </div>
                            </DataRow>
                        )}
                        <div className={styles.bodyContainer}>
                            {hasVisibleContentInBody(item) && (
                                <DataRow // Body
                                    iconName={ControlIcons.List}
                                    iconClassName={styles.contentIcon}
                                    className={styles.bodySection}
                                    iconContainerClassName={styles.iconContainer}
                                    childrenClassName={styles.bodyWrapper}>
                                    <div className={styles.body}>
                                        <MessageBody
                                            className={styles.messageBody}
                                            messageBody={santitizedHTMLBody}
                                            copyAllowed={true}
                                            printAllowed={true}
                                            isLoading={false}
                                            item={toCalendarItem(item)}
                                            externalMouseUpHandlerList={[]}
                                        />
                                    </div>
                                </DataRow>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </FocusTrapZone>
    );
});

/**
 * Returns the UI elements for the locations line
 */
function renderLocation(
    locations: EnhancedLocation[],
    item: CalendarEvent,
    colorScheme: ColorScheme
): JSX.Element {
    return (
        <InlineLocations
            locations={locations}
            actionSource={'ReadForm'}
            isOrganizer={item.IsOrganizer}
            isMeeting={item.IsMeeting}
            className={styles.locationValue}
            colorScheme={colorScheme}
        />
    );
}

/**
 * Event handler called when the view series button is clicked
 */
const onViewSeriesClicked = (item: CalendarEvent) => e => {
    fetchCalendarEventDetails(item, true /*fetchSeriesMaster*/);
};
